import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { rememberLeague } from "../lib/league";
import { timeZoneOptions, detectTimeZone, currentTimeIn } from "../shared/timezones";

interface HostStatus {
  canCreate: boolean;
  isHost: boolean;
  remaining: number | null;
  used: number;
  reason: string;
}

// Starting a crew.
//
// Anyone can JOIN a crew with an invite. Starting one needs a host code, because
// a host is someone who has agreed to run this for real people. Season 0 had a
// stray path that let any member start one with no conversation at all, and this
// screen is where that is closed: it either takes a code, explains the limit, or
// gets out of the way and lets a real host build their crew.
//
// The "were you invited?" card used to appear only via a firstTime prop, which
// Home set for someone who belonged to no crew at all. Gating makes that prop
// redundant: every person who lands on the code step is in exactly that
// situation, and someone who cannot start a crew and did not expect that is
// almost always someone who signed in with the wrong address. So the card is
// simply always there now, and the prop is gone.
export function NewLeague() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [host, setHost] = useState<HostStatus | null>(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [codeErr, setCodeErr] = useState("");

  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tz, setTz] = useState(detectTimeZone());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api<HostStatus>(`/api/league/admin`, { body: { op: "host-status" } })
      .then((s) => { if (alive) setHost(s); })
      .catch(() => {
        if (alive) {
          setHost({
            canCreate: false, isHost: false, remaining: 0, used: 0,
            reason: "We couldn't check your account just now. Check your connection and try again.",
          });
        }
      });
    return () => { alive = false; };
  }, []);

  async function redeem() {
    setRedeeming(true);
    setCodeErr("");
    try {
      const r = await api<HostStatus>(`/api/league/admin`, {
        body: { op: "redeem-host-code", code: code.trim(), displayName },
      });
      setHost(r);
    } catch (e: any) {
      setCodeErr(e?.message ?? "That didn't work. Try again in a moment.");
    } finally {
      setRedeeming(false);
    }
  }

  async function create() {
    setBusy(true);
    setErr("");
    try {
      const { data: u } = await supabase.auth.getUser();
      if (u.user && displayName) {
        await supabase
          .from("users")
          .upsert({ id: u.user.id, email: u.user.email ?? "", display_name: displayName }, { onConflict: "id" });
      }
      const r = await api<{ league: { id: string } }>(`/api/league/admin`, {
        body: { op: "create", name, timezone: tz, displayName },
      });
      if (!r?.league?.id) throw new Error("The server did not return a crew. Check the server settings in Cloudflare.");
      rememberLeague(r.league.id);
      // Through onboarding first, like everybody else. A host is a player too,
      // and creating a crew used to skip straight to the wizard, which is the
      // one path where nobody was ever asked about notifications. The host is
      // the last person who should miss a drop.
      navigate(`/league/${r.league.id}/onboarding`);
    } catch (e: any) {
      setErr(e?.message ?? "Something went wrong starting the crew.");
      setBusy(false);
    }
  }

  if (!host) {
    return <div className="app"><div className="loading">Loading…</div></div>;
  }

  // ---- Step 1: no grant yet. Take a code, and explain the likelier mix-up. ----
  if (!host.isHost) {
    return (
      <div className="app">
        <div className="header">
          <h1>Start a crew</h1>
          <button className="link" onClick={() => navigate("/")}>Cancel</button>
        </div>
        <p className="muted">
          Starting a crew needs a host code. Brian hands these out one at a time, so every
          crew has someone who knows how to run it. If you have one, enter it here.
        </p>

        <label className="muted">Host code</label>
        <input
          className="input"
          placeholder="Enter your code"
          value={code}
          autoCapitalize="none"
          autoCorrect="off"
          onChange={(e) => setCode(e.target.value)}
        />
        {codeErr && <p className="error">{codeErr}</p>}
        <button className="btn primary" disabled={redeeming || !code.trim()} onClick={redeem}>
          {redeeming ? "Checking…" : "Use this code"}
        </button>

        <div className="card">
          <span className="tag">Were you invited to a crew?</span>
          <p>
            Then you&apos;re in the wrong place, and you don&apos;t need a code. Open the invite
            link your friend sent you. If it asks you to sign in and nothing happens, it&apos;s
            usually because <b>{user?.email || "this email"}</b> isn&apos;t the address the
            invite went to.
          </p>
          <button className="btn ghost" onClick={async () => { await signOut(); window.location.href = "/"; }}>
            Sign out and use a different email
          </button>
        </div>
      </div>
    );
  }

  // ---- Step 2: a real host who has used up their code. ----
  if (!host.canCreate) {
    return (
      <div className="app">
        <div className="header">
          <h1>Start a crew</h1>
          <button className="link" onClick={() => navigate("/")}>Back</button>
        </div>
        <div className="card">
          <span className="tag">You&apos;ve used this code</span>
          <p>{host.reason}</p>
        </div>
        <button className="btn ghost" onClick={() => navigate("/")}>Back to your crews</button>
      </div>
    );
  }

  // ---- Step 3: cleared to build. ----
  return (
    <div className="app">
      <div className="header">
        <h1>Start a crew</h1>
        <button className="link" onClick={() => navigate("/")}>Cancel</button>
      </div>
      <p className="muted">
        You&apos;ll be the host, and a player like everybody else. You can invite your people on the
        next screen.
      </p>
      {host.remaining != null && (
        <p className="muted">
          Your code covers {host.remaining} more crew{host.remaining === 1 ? "" : "s"}.
        </p>
      )}

      <label className="muted">Crew name</label>
      <input className="input" placeholder="The Cousins" value={name} onChange={(e) => setName(e.target.value)} />

      <label className="muted">Your name, as your people will see it</label>
      <input className="input" placeholder="Your name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />

      <label className="muted">Timezone <span style={{ opacity: 0.7 }}>(drop times follow this)</span></label>
      <select className="input" value={tz} onChange={(e) => setTz(e.target.value)}>
        {timeZoneOptions(tz).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
      </select>
      {currentTimeIn(tz) && <p className="muted">Right now it is {currentTimeIn(tz)} there.</p>}

      {err && <p className="error">{err}</p>}
      <button className="btn primary" disabled={busy || !name.trim() || !displayName.trim()} onClick={create}>
        {busy ? "Starting…" : "Start the crew"}
      </button>
    </div>
  );
}
